import { missingVideoDetails, type VideoDetails } from "@athena/common/utils/videoDetails";
import type { VideoAssetStatus } from "@athena/common/graphql/generated/schema";

/**
 * The bulk uploader's queue, as a pure reducer.
 *
 * Every video goes through two independent tracks, and counts as **uploaded** only when both are
 * done (ADR 0014):
 *
 * - **Transfer and processing** — `phase`: queued → uploading → processing → ready.
 * - **Details** — `published`: the operator has given it a title, description, presenter and tags
 *   and the server has taken them, which is what takes it out of draft.
 *
 * The two run side by side on purpose. Waiting for a twenty-minute transcode before you may type a
 * title is how a mass upload turns into an afternoon.
 */

/**
 * What the form holds for one video: the required details, plus the two optional defaults its lessons
 * start from — restricted, and which brands see it (ADR 0014).
 */
export interface QueueDetails extends VideoDetails {
    restricted: boolean;
    /** Brand slugs. Empty means every brand. */
    brands: string[];
}

/** The details "copy to others" carries — everything but what names this one video. */
export type CopyableField = "presenterId" | "tagIds" | "restricted" | "brands";

export type UploadPhase =
    /** Waiting for a free upload slot. */
    | "queued"
    | "uploading"
    /** Bytes are in; the server is transcoding. */
    | "processing"
    | "ready"
    | "failed"
    /**
     * A draft recovered after a reload whose transfer never finished. The bytes went with the tab,
     * so the only way forward is to discard it and add the file again.
     */
    | "interrupted";

export interface QueueItem {
    /** Local identity — stable before the server has assigned an asset id, and across retries. */
    key: string;
    filename: string;
    sizeBytes: number;
    /** Null for a recovered draft: the bytes are not in this tab. Retry needs them. */
    file: File | null;
    /** Owner. Undefined lets the server place a platform admin's upload in the shared library. */
    organizationId: string | undefined;
    assetId: string | null;
    phase: UploadPhase;
    /** Fraction of the transfer done, 0–1. */
    progress: number;
    error: string | null;
    posterUrl: string | null;
    details: QueueDetails;
    /** The operator has typed something not yet saved — what the unload warning protects. */
    detailsDirty: boolean;
    published: boolean;
    publishing: boolean;
    /**
     * Finish was pressed before the bytes were in. Publishing needs the asset, so it waits and the
     * provider publishes the moment the transfer completes.
     */
    finishWhenUploaded: boolean;
}

export interface QueueState {
    items: QueueItem[];
}

export const EMPTY_QUEUE: QueueState = { items: [] };

/** A recovered draft, as `myVideoUploadDrafts` returns it. */
export interface RecoveredDraft {
    id: string;
    status: VideoAssetStatus;
    title?: string | null;
    originalFilename?: string | null;
    posterUrl?: string | null;
}

/** One asset's state as the processing poll saw it. */
export interface PolledAsset {
    id: string;
    status: VideoAssetStatus;
    posterUrl?: string | null;
}

export type QueueAction =
    | { type: "added"; items: { key: string; file: File }[]; organizationId: string | undefined }
    | { type: "recovered"; drafts: RecoveredDraft[]; keyFor: (assetId: string) => string }
    | { type: "started"; key: string }
    | { type: "created"; key: string; assetId: string }
    | { type: "progressed"; key: string; progress: number }
    | { type: "transferred"; key: string }
    | { type: "uploadFailed"; key: string; message: string }
    | { type: "polled"; requestedIds: string[]; assets: PolledAsset[] }
    | { type: "retried"; key: string }
    | { type: "removed"; key: string }
    | { type: "clearedFinished" }
    | { type: "detailsEdited"; key: string; patch: Partial<QueueDetails> }
    | { type: "detailsCopied"; fromKey: string; fields: CopyableField[] }
    | { type: "finishRequested"; key: string }
    | { type: "publishStarted"; key: string }
    | { type: "published"; key: string }
    | { type: "publishFailed"; key: string; message: string };

/**
 * "intro-to-urge-surfing_final.mp4" → "Intro to urge surfing final". A starting title the operator
 * will usually keep or lightly edit, which beats an empty box per video when there are forty.
 */
export const titleFromFilename = (filename: string): string => {
    const base = filename
        .replace(/\.[^./]+$/, "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return base ? base.charAt(0).toUpperCase() + base.slice(1) : filename;
};

const blankDetails = (title: string): QueueDetails => ({
    title,
    description: "",
    presenterId: null,
    tagIds: [],
    restricted: false,
    brands: [],
});

const PROCESSING_FAILED = "Processing failed — this file could not be converted for streaming.";
const UPLOAD_GONE = "This upload no longer exists. It may have been deleted from another tab.";
const INTERRUPTED = "The upload was interrupted before it finished. Discard it and add the file again.";

/** Where a server status puts an item that is past its transfer. */
const phaseForStatus = (status: VideoAssetStatus): UploadPhase | null => {
    if (status === "READY") return "ready";
    if (status === "PROCESSING") return "processing";
    if (status === "FAILED" || status === "DELETED") return "failed";
    return null;
};

const update = (state: QueueState, key: string, change: (item: QueueItem) => QueueItem): QueueState => ({
    items: state.items.map((item) => (item.key === key ? change(item) : item)),
});

export function queueReducer(state: QueueState, action: QueueAction): QueueState {
    switch (action.type) {
        case "added":
            return {
                items: [
                    ...state.items,
                    ...action.items.map(
                        ({ key, file }): QueueItem => ({
                            key,
                            filename: file.name,
                            sizeBytes: file.size,
                            file,
                            organizationId: action.organizationId,
                            assetId: null,
                            phase: "queued",
                            progress: 0,
                            error: null,
                            posterUrl: null,
                            details: blankDetails(titleFromFilename(file.name)),
                            detailsDirty: false,
                            published: false,
                            publishing: false,
                            finishWhenUploaded: false,
                        }),
                    ),
                ],
            };

        case "recovered": {
            // A draft this tab is already tracking keeps its local state — it knows more.
            const known = new Set(state.items.map((i) => i.assetId).filter(Boolean));
            const fresh = action.drafts
                .filter((d) => !known.has(d.id))
                .map((d): QueueItem => {
                    const phase = d.status === "UPLOADING" ? "interrupted" : (phaseForStatus(d.status) ?? "processing");
                    const filename = d.originalFilename ?? d.title ?? "Untitled video";
                    return {
                        key: action.keyFor(d.id),
                        filename,
                        sizeBytes: 0,
                        file: null,
                        organizationId: undefined,
                        assetId: d.id,
                        phase,
                        progress: 1,
                        error: phase === "interrupted" ? INTERRUPTED : phase === "failed" ? PROCESSING_FAILED : null,
                        posterUrl: d.posterUrl ?? null,
                        // The stored title is the filename unless somebody set one; derive the
                        // same friendly default a fresh upload gets.
                        details: blankDetails(
                            d.title && d.title !== d.originalFilename ? d.title : titleFromFilename(filename),
                        ),
                        detailsDirty: false,
                        published: false,
                        publishing: false,
                        finishWhenUploaded: false,
                    };
                });
            return fresh.length
                ? {
                      items: [
                          ...state.items,
                          ...fresh,
                      ],
                  }
                : state;
        }

        case "started":
            return update(state, action.key, (i) => ({ ...i, phase: "uploading", progress: 0, error: null }));

        case "created":
            // The drafts query can answer between `createVideoUpload` returning and this action,
            // listing the in-flight upload as an interrupted draft. This tab holds the real one.
            return {
                items: state.items
                    .filter((i) => i.key === action.key || i.assetId !== action.assetId)
                    .map((i) => (i.key === action.key ? { ...i, assetId: action.assetId } : i)),
            };

        case "progressed":
            return update(state, action.key, (i) => ({ ...i, progress: action.progress }));

        case "transferred":
            return update(state, action.key, (i) => ({ ...i, phase: "processing", progress: 1 }));

        case "uploadFailed":
            // The runner deleted the half-made asset, so a retry starts from nothing — and a Finish
            // pressed in the meantime has nothing left to wait for.
            return update(state, action.key, (i) => ({
                ...i,
                phase: "failed",
                assetId: null,
                error: action.message,
                finishWhenUploaded: false,
            }));

        case "polled": {
            const byId = new Map(
                action.assets.map((a) => [
                    a.id,
                    a,
                ]),
            );
            const requested = new Set(action.requestedIds);
            return {
                items: state.items.map((item) => {
                    if (!item.assetId || !requested.has(item.assetId)) return item;
                    const asset = byId.get(item.assetId);
                    // Asked about and not returned: deleted, or reaped after failing.
                    if (!asset) return { ...item, phase: "failed", assetId: null, error: UPLOAD_GONE };
                    const phase = phaseForStatus(asset.status) ?? item.phase;
                    return {
                        ...item,
                        phase,
                        posterUrl: asset.posterUrl ?? item.posterUrl,
                        error: phase === "failed" ? PROCESSING_FAILED : item.error,
                        // A failed asset is reaped by the server; nothing left to point at.
                        assetId: phase === "failed" ? null : item.assetId,
                    };
                }),
            };
        }

        case "retried":
            // A video whose details were already saved lost them with the failed asset. They are
            // still in the form, so the retry finishes itself rather than asking twice.
            return update(state, action.key, (i) =>
                i.file
                    ? {
                          ...i,
                          phase: "queued",
                          progress: 0,
                          error: null,
                          assetId: null,
                          published: false,
                          finishWhenUploaded: i.published || i.finishWhenUploaded,
                      }
                    : i,
            );

        case "removed":
            return { items: state.items.filter((i) => i.key !== action.key) };

        case "clearedFinished":
            return { items: state.items.filter((i) => !isUploaded(i)) };

        case "detailsEdited":
            return update(state, action.key, (i) => ({
                ...i,
                details: { ...i.details, ...action.patch },
                detailsDirty: true,
            }));

        case "detailsCopied": {
            const from = state.items.find((i) => i.key === action.fromKey);
            if (!from) return state;
            const patch: Partial<QueueDetails> = {};
            for (const field of action.fields) Object.assign(patch, { [field]: from.details[field] });
            return {
                items: state.items.map((i) =>
                    i.key !== from.key && canEditDetails(i)
                        ? { ...i, details: { ...i.details, ...patch }, detailsDirty: true }
                        : i,
                ),
            };
        }

        case "finishRequested":
            return update(state, action.key, (i) => ({ ...i, finishWhenUploaded: true, error: null }));

        case "publishStarted":
            return update(state, action.key, (i) => ({ ...i, publishing: true, finishWhenUploaded: false }));

        case "published":
            return update(state, action.key, (i) => ({
                ...i,
                publishing: false,
                published: true,
                detailsDirty: false,
                error: null,
            }));

        case "publishFailed":
            return update(state, action.key, (i) => ({ ...i, publishing: false, error: action.message }));
    }
}

// ---- Selectors ----

/** Both tracks done: processed, and described. */
export const isUploaded = (item: QueueItem): boolean => item.published && item.phase === "ready";

/** Bytes still moving or waiting to — what closing the tab would lose. */
export const isTransferring = (item: QueueItem): boolean => item.phase === "queued" || item.phase === "uploading";

/** Whether the details form is open for edits — not once saved, and not for a dead upload. */
export const canEditDetails = (item: QueueItem): boolean =>
    !item.published && !item.publishing && item.phase !== "failed" && item.phase !== "interrupted";

export const needsDetails = (item: QueueItem): boolean => canEditDetails(item);

/** The bytes are in, so the server has an asset to publish. */
export const isTransferred = (item: QueueItem): boolean =>
    !!item.assetId && (item.phase === "processing" || item.phase === "ready");

export const missingDetails = (item: QueueItem) => missingVideoDetails(item.details);

/** Items whose server status is still moving and worth polling. */
export const assetIdsToPoll = (state: QueueState): string[] =>
    state.items.flatMap((i) => (i.assetId && i.phase === "processing" ? [i.assetId] : []));

export interface QueueSummary {
    total: number;
    transferring: number;
    processing: number;
    needsDetails: number;
    failed: number;
    uploaded: number;
    /** Bytes sent over bytes to send, across everything still transferring or queued. */
    transferFraction: number;
}

export const summarize = (state: QueueState): QueueSummary => {
    let sent = 0;
    let toSend = 0;
    const summary: QueueSummary = {
        total: state.items.length,
        transferring: 0,
        processing: 0,
        needsDetails: 0,
        failed: 0,
        uploaded: 0,
        transferFraction: 1,
    };
    for (const item of state.items) {
        if (isTransferring(item)) {
            summary.transferring++;
            sent += item.sizeBytes * item.progress;
            toSend += item.sizeBytes;
        }
        if (item.phase === "processing") summary.processing++;
        if (item.phase === "failed" || item.phase === "interrupted") summary.failed++;
        if (needsDetails(item)) summary.needsDetails++;
        if (isUploaded(item)) summary.uploaded++;
    }
    summary.transferFraction = toSend > 0 ? sent / toSend : 1;
    return summary;
};

/** Same file chosen twice — name, size and modification time all match. */
export const isSameFile = (a: File, b: File): boolean =>
    a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

export type RejectedFile = { name: string; reason: "not-a-video" | "empty" | "duplicate" };

/**
 * Splits a drop into what can be queued and what cannot, with a reason for each refusal. The server
 * refuses a non-`video/*` content type, so it is caught here rather than after a round trip — and a
 * file already in the queue is skipped, because dropping a folder twice is easy and two copies of
 * every video is not what anybody meant.
 */
export const partitionFiles = (
    files: readonly File[],
    queued: readonly QueueItem[],
): { accepted: File[]; rejected: RejectedFile[] } => {
    const accepted: File[] = [];
    const rejected: RejectedFile[] = [];
    const seen = queued.flatMap((i) => (i.file ? [i.file] : []));
    for (const file of files) {
        if (!file.type.startsWith("video/")) rejected.push({ name: file.name, reason: "not-a-video" });
        else if (file.size === 0) rejected.push({ name: file.name, reason: "empty" });
        else if (
            [
                ...seen,
                ...accepted,
            ].some((f) => isSameFile(f, file))
        )
            rejected.push({ name: file.name, reason: "duplicate" });
        else accepted.push(file);
    }
    return { accepted, rejected };
};
